/**
 * AI APIs → one provider (A7Y-5559): a month at a time (`?month=YYYY-MM`,
 * defaulting to the newest month with rows), split by model, by API key, and
 * checked against our own call log. Gemini adds the billed SKUs the mapper
 * could not attribute to a model; Perplexity, which has no usage API, adds the
 * Ledger's credit top-ups for the month.
 *
 * Key hints are the provider's own redacted form and are rendered exactly as
 * returned — never expanded, re-masked or truncated.
 */
import { useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import {
  isAiApiProvider,
  type AiApiProvider,
  type AiApiProviderMonthDetailResponse,
} from '@a7y/types';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { SortableHeader } from '@/components/SortableHeader';
import { ViewQueryButton } from '@/pages/audit-spend/ViewQueryButton';
import { formatInt, formatPct, formatUsd2 } from '@/pages/audit-spend/format';
import {
  CARD_CLASSES,
  EMPTY_PROVIDER_MESSAGE,
  formatMonthLong,
  providerInfo,
  sourceLabel,
  useSortedRows,
} from './meta';
import { ProviderGlyph } from './provider-meta';
import { BasisBadge, DataAge, ErrorBanner, SourceBadge } from './shared';

const BACK_LINK = (
  <Link
    to="/admin/ai-apis/overview"
    className="mb-4 inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
  >
    <ArrowLeft className="mr-1 h-4 w-4" /> All providers
  </Link>
);

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

type Detail = AiApiProviderMonthDetailResponse;

function SectionCard({
  title,
  caption,
  children,
  testId,
}: {
  title: string;
  caption?: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <section className={CARD_CLASSES} data-testid={testId}>
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
      {caption ? <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{caption}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

const num = (n: number) => <span className="tabular-nums">{formatInt(n)}</span>;
const usd = (n: number) => <span className="tabular-nums">{formatUsd2(n)}</span>;

type ModelField =
  'model' | 'source' | 'input' | 'output' | 'cacheRead' | 'cacheWrite' | 'requests' | 'usd';

function ModelsTable({ rows }: { rows: Detail['models'] }) {
  const { sorted, ...sort } = useSortedRows<Detail['models'][number], ModelField>(
    rows,
    'usd',
    (r, f) =>
      f === 'model'
        ? r.model
        : f === 'source'
          ? sourceLabel(r.source)
          : f === 'input'
            ? r.inputTokens
            : f === 'output'
              ? r.outputTokens
              : f === 'cacheRead'
                ? r.cacheReadTokens
                : f === 'cacheWrite'
                  ? r.cacheWriteTokens
                  : f === 'requests'
                    ? r.requests
                    : r.costUsd
  );
  const header = (field: ModelField, label: string) => (
    <SortableHeader field={field} label={label} {...sort} />
  );
  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          {header('model', 'Model')}
          {header('source', 'Source')}
          {header('input', 'Input')}
          {header('output', 'Output')}
          {header('cacheRead', 'Cache read')}
          {header('cacheWrite', 'Cache write')}
          {header('requests', 'Requests')}
          {header('usd', 'USD')}
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((r) => (
          <TableRow key={`${r.model}:${r.source}`}>
            <TableCell className="font-mono text-xs">{r.model}</TableCell>
            <TableCell>
              <SourceBadge source={r.source} />
            </TableCell>
            <TableCell>{num(r.inputTokens)}</TableCell>
            <TableCell>{num(r.outputTokens)}</TableCell>
            <TableCell>{num(r.cacheReadTokens)}</TableCell>
            <TableCell>{num(r.cacheWriteTokens)}</TableCell>
            <TableCell>{num(r.requests)}</TableCell>
            <TableCell>{usd(r.costUsd)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

type KeyField = 'hint' | 'label' | 'input' | 'output' | 'cache' | 'usd';

function KeysTable({ rows }: { rows: Detail['keys'] }) {
  const { sorted, ...sort } = useSortedRows<Detail['keys'][number], KeyField>(
    rows,
    'usd',
    (r, f) =>
      f === 'hint'
        ? r.apiKeyHint
        : f === 'label'
          ? r.keyLabel
          : f === 'input'
            ? r.inputTokens
            : f === 'output'
              ? r.outputTokens
              : f === 'cache'
                ? r.cacheReadTokens + r.cacheWriteTokens
                : r.costUsd
  );
  const header = (field: KeyField, label: string) => (
    <SortableHeader field={field} label={label} {...sort} />
  );
  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          {header('hint', 'Key')}
          {header('label', 'Label')}
          {header('input', 'Input')}
          {header('output', 'Output')}
          {header('cache', 'Cache')}
          {header('usd', 'USD')}
          <TableHeader>Share</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((r, i) => (
          <TableRow key={`${r.apiKeyId ?? ''}:${r.apiKeyHint ?? ''}:${r.keyLabel ?? ''}:${i}`}>
            <TableCell className="font-mono text-xs" data-testid="key-hint">
              {r.apiKeyHint ?? <span className="font-sans text-zinc-400">no hint reported</span>}
            </TableCell>
            <TableCell>{r.keyLabel ?? <span className="text-zinc-400">unlabelled</span>}</TableCell>
            <TableCell>{num(r.inputTokens)}</TableCell>
            <TableCell>{num(r.outputTokens)}</TableCell>
            <TableCell>{num(r.cacheReadTokens + r.cacheWriteTokens)}</TableCell>
            <TableCell>{usd(r.costUsd)}</TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-20 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                  <div
                    className="h-full rounded-full bg-zinc-500 dark:bg-zinc-400"
                    style={{ width: `${Math.min(100, Math.max(0, r.share * 100))}%` }}
                  />
                </div>
                <span className="tabular-nums text-xs">{formatPct(r.share, 0)}</span>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

type CrossCheckField = 'env' | 'requests' | 'callLog' | 'provider';

function CrossCheckTable({ rows }: { rows: Detail['crossCheck'] }) {
  const { sorted, ...sort } = useSortedRows<Detail['crossCheck'][number], CrossCheckField>(
    rows,
    'callLog',
    (r, f) =>
      f === 'env'
        ? r.env
        : f === 'requests'
          ? r.callLogRequests
          : f === 'callLog'
            ? r.callLogCostUsd
            : r.providerCostUsd
  );
  const header = (field: CrossCheckField, label: string) => (
    <SortableHeader field={field} label={label} {...sort} />
  );
  const delta = (callLog: number, provider: number | null) =>
    provider === null
      ? '—'
      : `${callLog - provider >= 0 ? '+' : '−'}${formatUsd2(Math.abs(callLog - provider))}`;
  const totalCallLog = rows.reduce((s, r) => s + r.callLogCostUsd, 0);
  const totalRequests = rows.reduce((s, r) => s + r.callLogRequests, 0);
  const providerUsd = rows[0]?.providerCostUsd ?? null;
  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          {header('env', 'Env')}
          {header('requests', 'Call-log requests')}
          {header('callLog', 'Call-log USD')}
          {header('provider', 'Provider USD')}
          <TableHeader>Delta</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((r) => (
          <TableRow key={r.env}>
            <TableCell>{r.env}</TableCell>
            <TableCell>{num(r.callLogRequests)}</TableCell>
            <TableCell>{usd(r.callLogCostUsd)}</TableCell>
            <TableCell>{r.providerCostUsd === null ? '—' : usd(r.providerCostUsd)}</TableCell>
            <TableCell className="tabular-nums">
              {rows.length > 1 ? '—' : delta(r.callLogCostUsd, r.providerCostUsd)}
            </TableCell>
          </TableRow>
        ))}
        {rows.length > 1 ? (
          <TableRow>
            <TableCell className="font-medium">All environments</TableCell>
            <TableCell>{num(totalRequests)}</TableCell>
            <TableCell>{usd(totalCallLog)}</TableCell>
            <TableCell>{providerUsd === null ? '—' : usd(providerUsd)}</TableCell>
            <TableCell className="tabular-nums">{delta(totalCallLog, providerUsd)}</TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

type SkuField = 'sku' | 'usd';

function UnmappedSkusTable({ rows }: { rows: Detail['unmappedSkus'] }) {
  const { sorted, ...sort } = useSortedRows<Detail['unmappedSkus'][number], SkuField>(
    rows,
    'usd',
    (r, f) => (f === 'sku' ? r.sku : r.costUsd)
  );
  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          <SortableHeader field="sku" label="SKU" {...sort} />
          <SortableHeader field="usd" label="USD" {...sort} />
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((s) => (
          <TableRow key={s.sku}>
            <TableCell className="font-mono text-xs">{s.sku}</TableCell>
            <TableCell>{usd(s.costUsd)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

type LedgerField = 'date' | 'description' | 'amount';

function LedgerTable({ rows }: { rows: Detail['ledgerRows'] }) {
  const { sorted, ...sort } = useSortedRows<Detail['ledgerRows'][number], LedgerField>(
    rows,
    'date',
    (r, f) => (f === 'date' ? r.date : f === 'description' ? r.description : r.amountUsd)
  );
  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          <SortableHeader field="date" label="Date" {...sort} />
          <SortableHeader field="description" label="Description" {...sort} />
          <SortableHeader field="amount" label="Amount" {...sort} />
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((r) => (
          <TableRow key={`${r.date}:${r.sheetUrl}`}>
            <TableCell>
              <a
                href={r.sheetUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                {r.date}
              </a>
            </TableCell>
            <TableCell>
              {r.description || <span className="text-zinc-400">no description</span>}
            </TableCell>
            <TableCell>{usd(r.amountUsd)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function ProviderDetailPage() {
  const { provider: providerParam } = useParams<{ provider: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const valid = isAiApiProvider(providerParam);
  const provider = (valid ? providerParam : 'anthropic') as AiApiProvider;
  const forceRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  const monthsQ = useQuery({
    queryKey: ['ai-apis', 'provider-months', provider],
    queryFn: () => api.getAiApiProviderMonths(provider, { force: forceRef.current }),
    enabled: valid,
  });
  const months = monthsQ.data?.data.months ?? [];
  const requested = searchParams.get('month');
  const month = requested && MONTH_PATTERN.test(requested) ? requested : months[0]?.month;

  const detailQ = useQuery({
    queryKey: ['ai-apis', 'provider-month', provider, month],
    queryFn: () =>
      api.getAiApiProviderMonth(provider, month as string, { force: forceRef.current }),
    enabled: valid && Boolean(month),
  });

  if (!valid) {
    return (
      <div className="space-y-4 px-4">
        {BACK_LINK}
        <PageHeader
          title="Unknown provider"
          subtitle={`No AI API provider named “${providerParam ?? ''}”.`}
        />
        <div className="rounded-lg border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
          This section covers Anthropic, OpenAI, Gemini and Perplexity. The link may be stale.
        </div>
      </div>
    );
  }

  const info = providerInfo(provider);
  const summary = months.find((m) => m.month === month);
  const detail = detailQ.data?.data;

  const refresh = async () => {
    forceRef.current = true;
    setRefreshing(true);
    try {
      await Promise.all([monthsQ.refetch(), detailQ.refetch()]);
    } finally {
      forceRef.current = false;
      setRefreshing(false);
    }
  };

  const header = (
    <div>
      {BACK_LINK}
      <PageHeader title={info.label} subtitle="LLM API usage and spend for one provider, by month">
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ProviderGlyph provider={provider} />
          <Button outline href={info.dashboardUrl} target="_blank" rel="noreferrer">
            Open {info.label} usage dashboard <ExternalLink data-slot="icon" />
          </Button>
          {info.billingUrl ? (
            <Button outline href={info.billingUrl} target="_blank" rel="noreferrer">
              Open {info.label} billing <ExternalLink data-slot="icon" />
            </Button>
          ) : null}
        </div>
      </PageHeader>
    </div>
  );

  if (monthsQ.isError) {
    return (
      <div className="space-y-4 px-4">
        {header}
        <ErrorBanner
          endpoint={`/admin/ai-apis/providers/${provider}/months`}
          error={monthsQ.error}
        />
      </div>
    );
  }

  if (monthsQ.isLoading) {
    return (
      <div className="space-y-4 px-4">
        {header}
        <div className="text-sm text-zinc-500">Loading months…</div>
      </div>
    );
  }

  if (months.length === 0) {
    return (
      <div className="space-y-4 px-4">
        {header}
        <div className={CARD_CLASSES} data-testid="provider-detail-empty">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{EMPTY_PROVIDER_MESSAGE}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4">
      {header}

      <div
        className={`${CARD_CLASSES} flex flex-wrap items-center gap-x-6 gap-y-3`}
        data-testid="month-summary"
      >
        <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
          Month
          <select
            aria-label="Month"
            value={month}
            onChange={(e) => setSearchParams({ month: e.target.value })}
            className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            {months.map((m) => (
              <option key={m.month} value={m.month}>
                {formatMonthLong(m.month)}
              </option>
            ))}
            {month && !summary ? <option value={month}>{formatMonthLong(month)}</option> : null}
          </select>
        </label>
        {summary ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums text-zinc-900 dark:text-white">
                {formatUsd2(summary.costUsd)}
              </span>
              <BasisBadge basis={summary.costBasis} />
            </div>
            <div className="text-xs text-zinc-600 dark:text-zinc-400 tabular-nums">
              {formatInt(summary.inputTokens)} in · {formatInt(summary.outputTokens)} out ·{' '}
              {formatInt(summary.cacheReadTokens + summary.cacheWriteTokens)} cache ·{' '}
              {formatInt(summary.requests)} requests
            </div>
            <div className="flex items-center gap-1">
              {summary.sources.map((s) => (
                <SourceBadge key={s} source={s} />
              ))}
            </div>
          </>
        ) : (
          <span className="text-xs text-zinc-500">No rows for this month.</span>
        )}
        <div className="ml-auto flex items-center gap-3">
          <DataAge generatedAt={detailQ.data?.generated_at} />
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={refreshing}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {detailQ.isError ? (
        <ErrorBanner
          endpoint={`/admin/ai-apis/providers/${provider}/months/${month}`}
          error={detailQ.error}
        />
      ) : null}
      {detailQ.isLoading ? (
        <div className="text-sm text-zinc-500">Loading {month ? formatMonthLong(month) : ''}…</div>
      ) : null}

      {detail ? (
        <>
          <SectionCard title="Models" testId="models-table">
            {detail.models.length ? (
              <ModelsTable rows={detail.models} />
            ) : (
              <p className="text-sm text-zinc-500">No model rows this month.</p>
            )}
          </SectionCard>

          {info.hasKeyDimension ? (
            <SectionCard
              title="API keys"
              testId="keys-table"
              caption="Split from the provider's own per-key usage; hints are shown exactly as the provider reports them."
            >
              {detail.keys.length ? (
                <KeysTable rows={detail.keys} />
              ) : (
                <p className="text-sm text-zinc-500">No per-key usage this month.</p>
              )}
            </SectionCard>
          ) : (
            <p
              className="px-1 text-xs text-zinc-500 dark:text-zinc-400"
              data-testid="single-key-note"
            >
              {info.label} has one API key ({info.keySecretNames.join(', ')}), so there is no
              per-key split.
            </p>
          )}

          <SectionCard
            title="Cross-check"
            testId="cross-check-table"
            caption="The call log is our own count of the calls we made; the provider figure is the authority."
          >
            {detail.crossCheck.length ? (
              <CrossCheckTable rows={detail.crossCheck} />
            ) : (
              <p className="text-sm text-zinc-500">No call-log rows this month.</p>
            )}
          </SectionCard>

          {provider === 'gemini' && detail.unmappedSkus.length ? (
            <SectionCard
              title="Unmapped SKUs"
              testId="unmapped-skus"
              caption="Billed SKUs the mapper could not attribute to a model."
            >
              <UnmappedSkusTable rows={detail.unmappedSkus} />
            </SectionCard>
          ) : null}

          {provider === 'perplexity' ? (
            <SectionCard
              title="Ledger"
              testId="ledger-table"
              caption="Credit top-ups recorded in the A7Y Ledger this month."
            >
              {detail.ledgerRows.length ? (
                <LedgerTable rows={detail.ledgerRows} />
              ) : (
                <p className="text-sm text-zinc-500">
                  No Perplexity top-ups in the Ledger this month.
                </p>
              )}
            </SectionCard>
          ) : null}

          <div className="px-1">
            <ViewQueryButton query={detailQ.data?.query} />
          </div>
        </>
      ) : null}
    </div>
  );
}
